import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, Briefcase } from "lucide-react";
import { money } from "@/lib/site";
import { RatingBadge } from "@/components/Reviews";

export const MentorAvatar = ({ mentor, className = "w-16 h-16" }) => (
  mentor.photo ? (
    <img src={mentor.photo} alt={mentor.name} className={`${className} rounded-2xl object-cover shrink-0`} />
  ) : (
    <div className={`${className} rounded-2xl bg-[#0B2F55] text-[#F5F1E8] flex items-center justify-center font-heading font-extrabold text-xl shrink-0`}>
      {(mentor.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
    </div>
  )
);

export const MentorCard = ({ mentor, index = 0, currency = "INR" }) => {
  const navigate = useNavigate();
  const cur = mentor.currency || currency;
  return (
    <motion.article initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.06 }}
      onClick={() => navigate(`/mentors/${mentor.slug}`)} data-testid={`mentor-card-${mentor.slug}`}
      className="group cursor-pointer bg-white rounded-3xl border border-slate-200 p-6 flex flex-col hover:-translate-y-1 hover:shadow-xl hover:border-primary/30 transition-all">
      <div className="flex items-start gap-4">
        <MentorAvatar mentor={mentor} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-heading font-bold text-lg text-slate-900 truncate">{mentor.name}</h3>
            {mentor.featured && <Star className="w-4 h-4 text-amber-400 fill-amber-400 shrink-0" data-testid="featured-star" />}
          </div>
          <p className="font-body text-sm text-slate-500 line-clamp-2 mt-0.5">{mentor.headline}</p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
            {mentor.experience_years > 0 && (
              <p className="font-body text-xs text-slate-400 inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {mentor.experience_years}+ yrs experience</p>
            )}
            <RatingBadge rating={mentor.rating} count={mentor.review_count} testId={`rating-${mentor.slug}`} />
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-4">
        {(mentor.expertise || []).slice(0, 4).map((t) => (
          <span key={t} className="font-body text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">{t}</span>
        ))}
      </div>
      <div className="mt-auto pt-5 flex items-end justify-between">
        <div>
          {mentor.session_price > 0 && <p className="font-heading font-extrabold text-slate-900">{money(mentor.session_price, cur)} <span className="font-body font-normal text-xs text-slate-400">/ session</span></p>}
          {mentor.monthly_price > 0 && <p className="font-body text-xs text-slate-500">{money(mentor.monthly_price, cur)} / month</p>}
        </div>
        <span className="font-heading font-bold text-sm text-primary inline-flex items-center gap-1 group-hover:gap-2 transition-all">View <ArrowRight className="w-4 h-4" /></span>
      </div>
    </motion.article>
  );
};
